import React, { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw, QrCode } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { QRCodeCanvas } from "qrcode.react";

import { functions } from "../../../firebase";
import { TENANT_ID } from "../../../tenant";

console.log("[MemberQRCode] module loaded");

const REFRESH_SAFETY_SEC = 8; // 少し早めに更新
const MIN_REFRESH_WAIT_SEC = 5;

const MemberQRCode = ({ user, profile, activeItem }) => {
  const [qrValue, setQrValue] = useState("");
  const [expSec, setExpSec] = useState(0);
  const [ttlSec, setTtlSec] = useState(60);
  const [progress, setProgress] = useState(100);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshTimerRef = useRef(null);
  const refreshRef = useRef(null);
  const cancelledRef = useRef(false);
  const mountIdRef = useRef(Math.random().toString(16).slice(2));

  useEffect(() => {
    const mountId = mountIdRef.current;
    console.log("[MemberQRCode] MOUNT", mountId);
    return () => console.log("[MemberQRCode] UNMOUNT", mountId);
  }, []);

  const clearTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback((nextTtlSec) => {
    const ttl = Number(nextTtlSec || 60);
    const waitSec = Math.max(MIN_REFRESH_WAIT_SEC, ttl - REFRESH_SAFETY_SEC);

    clearTimer();
    refreshTimerRef.current = setTimeout(() => {
      if (refreshRef.current) refreshRef.current();
    }, waitSec * 1000);
  }, [clearTimer]);

  useEffect(() => {
    if (!user?.uid) {
      setQrValue("");
      setExpSec(0);
      setTtlSec(60);
      setProgress(0);
      return;
    }

    cancelledRef.current = false;

    const issueQrToken = httpsCallable(functions, "issueQrToken");

    const refresh = async () => {
      if (cancelledRef.current) return;

      try {
        setIsRefreshing(true);

        const res = await issueQrToken({ tenantId: TENANT_ID });
        const payload = res?.data?.result ?? res?.data ?? {};

        const jwtToken = String(payload.token || "").trim();
        const exp = Number(payload.exp || 0);
        const ttl = Number(payload.ttl || 60);

        if (cancelledRef.current) return;

        if (!jwtToken || !exp || !ttl) {
          throw new Error("invalid issueQrToken response");
        }

        setQrValue(jwtToken);
        setExpSec(exp);
        setTtlSec(ttl);

        console.log(
          "[QR] token head:",
          jwtToken.slice(0, 24),
          "len:",
          jwtToken.length,
          "ttl:",
          ttl,
          "exp:",
          exp
        );

        scheduleNext(ttl);
      } catch (e) {
        console.error("[issueQrToken] error", e);

        // 期限切れしているなら古いQRを残さない
        const now = Math.floor(Date.now() / 1000);
        if (expSec > 0 && now >= expSec) {
          setQrValue("");
        }

        scheduleNext(ttlSec || 60);
      } finally {
        if (!cancelledRef.current) {
          setIsRefreshing(false);
        }
      }
    };

    refreshRef.current = refresh;
    refresh();

    return () => {
      cancelledRef.current = true;
      clearTimer();
      refreshRef.current = null;
    };
  }, [user?.uid, expSec, ttlSec, scheduleNext, clearTimer]);

  // 前面復帰時に即更新
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && refreshRef.current) {
        console.log("[MemberQRCode] visibility refresh");
        refreshRef.current();
      }
    };

    const onFocus = () => {
      if (refreshRef.current) {
        console.log("[MemberQRCode] focus refresh");
        refreshRef.current();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // 進捗バー + 期限切れ時の無効化
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now() / 1000;

      if (expSec > 0 && ttlSec > 0) {
        const remaining = Math.max(0, expSec - now);
        setProgress((remaining / ttlSec) * 100);

        if (remaining <= 0) {
          setQrValue("");
          if (refreshRef.current) {
            refreshRef.current();
          }
        }
      } else {
        setProgress(0);
      }
    }, 200);

    return () => clearInterval(t);
  }, [expSec, ttlSec]);

  return (
    <div
      className={[
        "bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans transition-all duration-500 relative overflow-hidden",
        activeItem ? "ring-1 ring-gray-200" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-1 px-0.5 text-gray-900">
        <div className="w-6 h-6 flex items-center justify-center">
          <QrCode size={15} />
        </div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">
          入り口でQRをかざしてください
        </p>
      </div>

      <div className="w-full flex items-center justify-center mt-1">
        {qrValue ? (
          <div className="bg-white rounded-3xl">
            <QRCodeCanvas
              value={qrValue}
              size={200}
              marginSize={1}
              level="M"
            />
          </div>
        ) : (
          <div className="w-40 h-40 flex items-center justify-center text-[10px] text-gray-500">
            更新中…
          </div>
        )}
      </div>

      <div className="w-full space-y-3 px-1">
        <div className="flex justify-center items-center text-[8px] font-black text-gray-500 tracking-widest uppercase">
          <div className="flex items-center gap-1 text-gray-900 font-black">
            <RefreshCw
              size={10}
              className={isRefreshing ? "animate-spin-slow" : ""}
            />
            <span>{isRefreshing ? "Syncing" : "Ready"}</span>
          </div>
        </div>

        <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200/30">
          <div
            className="h-full bg-gray-900 rounded-full transition-all duration-100 ease-linear"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default MemberQRCode;

/*import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, QrCode } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { QRCodeCanvas } from "qrcode.react";

import { functions } from "../../../firebase";
import { TENANT_ID } from "../../../tenant";

console.log("[MemberQRCode] module loaded");

const REFRESH_SAFETY_SEC = 3; // 期限の数秒前に更新

const MemberQRCode = ({ user, profile, activeItem }) => {
  // QRに入れる値
  // Functions が shortUrl を返せるなら shortUrl を優先
  // 返せない間は token をフォールバックで使う
  const [qrValue, setQrValue] = useState("");
  const [expSec, setExpSec] = useState(0); // UNIX秒
  const [ttlSec, setTtlSec] = useState(30);
  const [progress, setProgress] = useState(100);

  const refreshTimerRef = useRef(null);
  const mountIdRef = useRef(Math.random().toString(16).slice(2));

  useEffect(() => {
    const mountId = mountIdRef.current;
    console.log("[MemberQRCode] MOUNT", mountId);
    return () => console.log("[MemberQRCode] UNMOUNT", mountId);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    const fn = functions;
    const issueQrToken = httpsCallable(fn, "issueQrToken");

    const clearTimer = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    const scheduleNext = (nextTtlSec) => {
      const ttl = Number(nextTtlSec || ttlSec || 30);
      const waitSec = Math.max(5, ttl - REFRESH_SAFETY_SEC);

      clearTimer();
      refreshTimerRef.current = setTimeout(() => {
        refresh();
      }, waitSec * 1000);
    };

    const refresh = async () => {
      try {
        const res = await issueQrToken({ tenantId: TENANT_ID });
        console.log("[issueQrToken] res =", res);

        // { data: { result: {...} } } と { data: {...} } の両方を吸収
        const payload = res?.data?.result ?? res?.data ?? {};

        const jwtToken = String(payload.token || "").trim();
        const shortUrl = String(payload.shortUrl || "").trim();
        const exp = Number(payload.exp || 0);
        const ttl = Number(payload.ttl || 60);

        if (cancelled) return;

        // shortUrl を最優先。まだ未対応なら token を利用
        const nextQrValue = shortUrl || jwtToken;

        if (nextQrValue) {
          setQrValue(nextQrValue);
          setExpSec(exp);
          setTtlSec(ttl);

          console.log(
            "[QR] value head:",
            nextQrValue.slice(0, 32),
            "len:",
            nextQrValue.length,
            "ttl:",
            ttl,
            "mode:",
            shortUrl ? "shortUrl" : "token"
          );
        }

        scheduleNext(ttl);
      } catch (e) {
        console.error("[issueQrToken] error", e);
        scheduleNext(ttlSec || 60);
      }
    };

    refresh();

    return () => {
      cancelled = true;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // 進捗バー
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now() / 1000;

      if (expSec > 0 && ttlSec > 0) {
        const remaining = Math.max(0, expSec - now);
        setProgress((remaining / ttlSec) * 100);
      } else {
        const interval = ttlSec || 30;
        const currentBlock = Math.floor(now / interval);
        const remaining = (currentBlock + 1) * interval - now;
        setProgress((remaining / interval) * 100);
      }
    }, 100);

    return () => clearInterval(t);
  }, [expSec, ttlSec]);

  return (
    <div
      className={[
        "bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans transition-all duration-500 relative overflow-hidden",
        activeItem ? "ring-1 ring-gray-200" : "",
      ].join(" ")}
    >

      <div className="flex items-center gap-1 px-0.5 text-gray-900">
        <div className="w-6 h-6 flex items-center justify-center">
          <QrCode size={15} />
        </div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">
          入り口でQRをかざしてください
        </p>
      </div>


      <div className="w-full flex items-center justify-center mt-1">
        {qrValue ? (
          <div className="bg-white rounded-3xl">
            <QRCodeCanvas
              value={qrValue}
              size={200}
              marginSize={1}
              level="M"
            />
          </div>
        ) : (
          <div className="w-40 h-40 flex items-center justify-center text-[10px] text-gray-500">
            発行中…
          </div>
        )}
      </div>


      <div className="w-full space-y-3 px-1">
        <div className="flex justify-center items-center text-[8px] font-black text-gray-500 tracking-widest uppercase">
          <div className="flex items-center gap-1 text-gray-900 font-black">
            <RefreshCw size={10} className="animate-spin-slow" />
            <span>Syncing</span>
          </div>
        </div>

        <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200/30">
          <div
            className="h-full bg-gray-900 rounded-full transition-all duration-100 ease-linear"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default MemberQRCode;
*/